import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { environment } from 'src/environments/environment';
import {
  GetAllRolesResponse,
  GetAllUsers,
  GetUser,
  UserAdmin,
} from '../../models/admin';
import { ApiResponse } from '../../models/common/api.common.model';
import { CommonUtils } from '../../utils/common/common.utils';
import { ErrorhandleService } from '../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  baseUrl = environment.apiUrl.concat('user-management/');

  private readonly route = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly commonUtils = inject(CommonUtils);
  private readonly errorHandler = inject(ErrorhandleService);

  private currentUserSubject$: BehaviorSubject<UserAdmin> = new BehaviorSubject(
    {} as UserAdmin,
  );

  getCurrentUser$(): Observable<UserAdmin> {
    return this.currentUserSubject$.asObservable();
  }

  setCurrentUser$(user: UserAdmin) {
    this.currentUserSubject$.next(user);
  }

  /**
   *Get a list of all users
   *
   * @return {*}  {Observable<GetAllUsers>}
   * @memberof UserService
   */
  getAllUsers(locationCode?: string): Observable<GetAllUsers> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllUsers>(
        this.baseUrl.concat('get-users'),
        locationCode
          ? {
              params: {
                locationCode: locationCode,
              },
            }
          : undefined,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllUsers>(
            error,
            error.status,
            'User Service: Get All Users',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific user passing in the loginId.
   *Used mainly to get currently logged in user
   * @param
   * @return {*}  {Observable<GetUser>}
   * @memberof UserService
   */
  getUser(): Observable<GetUser> {
    this.commonUtils.updateLoadingModal(true);
    return this.http.get<GetUser>(this.baseUrl.concat('get-user')).pipe(
      take(1),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (
            (error.error?.message as string)
              .toLowerCase()
              .startsWith('invalid user')
          ) {
            this.route.navigate(['login'], {
              queryParams: {
                userId: (error.error?.message as string).slice(13),
              },
            });
            return of();
          }
        }
        return this.errorHandler.handleError<GetUser>(
          error,
          error.status,
          'User Service: Get User',
          BANNER_TYPE.GET_CURRENT_USER_ERROR,
          {} as GetUser,
        );
      }),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  /**
   *Creates a new user with the passed in user and logged in user's id
   *
   * @param {UserAdmin} user
   * @return {*}  {Observable<any>}
   * @memberof UserService
   */
  createUser(user: UserAdmin): Observable<GetUser> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<GetUser>(this.baseUrl.concat('add-user'), { user: user })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetUser>(
            error,
            error.status,
            'User Service: Create User',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
        filter((value) => !!value),
      );
  }

  /**
   *Edits passed in user with logged in user's id
   *
   * @param {UserAdmin} user
   * @return {*}  {Observable<any>}
   * @memberof UserService
   */
  editUser(user: UserAdmin): Observable<GetUser> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .put<GetUser>(this.baseUrl.concat('edit-user'), { user: user })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetUser>(
            error,
            error.status,
            'User Service: Edit User',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
          //On Editing User (mainly user location), close all active error banners
          this.commonUtils.updateBanner(false);
        }),
        filter((value) => !!value),
      );
  }

  /**
   *Deletes passed in user with logged in user's id
   *
   * @param {string} userId
   * @return {*}  {Observable<any>}
   * @memberof UserService
   */
  deleteUser(userId: string): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .delete<ApiResponse>(this.baseUrl.concat('delete-user'), {
        params: { deleteUserId: userId },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'User Service: Delete User',
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  getAllRoles(): Observable<GetAllRolesResponse> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<GetAllRolesResponse>(this.baseUrl.concat('getallroles'))
      .pipe(
        take(1),
        catchError((error) =>
          //Since this api is a prerequisite for EVERY other api.  If this fails
          //we should show a different error then the standard error message
          this.errorHandler.handleError<GetAllRolesResponse>(
            error,
            null,
            'User Service: Get All Roles',
            BANNER_TYPE.ROLES_UNSUCCESSFUL,
            {} as GetAllRolesResponse,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
